import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import { z } from 'zod';

import { AssignmentResultScreen } from '@/features/assessments/components/shared/AssignmentResultScreen';
import { requirePermission } from '@/lib/guards';

export const Route = createFileRoute('/app/assessments/assignments/$assignmentId')({
  validateSearch: z.object({
    // Missing on a hand-typed link; the screen then shows its error state, not a crash.
    assessmentId: z.string().catch(''),
    clientId: z.string().optional(),
    // Set by timeline bubbles so the back link returns to the originating
    // surface — `history` → client detail History tab; `sessions` → the
    // sessions workspace. Falls back to the assignments list when absent.
    returnTab: z.enum(['history', 'sessions']).optional(),
  }),
  beforeLoad: () => {
    requirePermission('assessment:read');
  },
  component: Wrap,
  staticData: { crumb: 'Result' },
});

function Wrap() {
  const { assignmentId } = useParams({ from: '/app/assessments/assignments/$assignmentId' });
  const { assessmentId, clientId, returnTab } = useSearch({
    from: '/app/assessments/assignments/$assignmentId',
  });
  // When returnTab is set the user reached this screen via a timeline
  // bubble — `useHistoryBack` makes the back link call `router.history.back()`
  // so they land on the exact previous URL (correct session in drawer, etc.)
  // rather than a freshly-built `/app/sessions` listing.
  let backTo: {
    to: string;
    label: string;
    params?: Record<string, string>;
    search?: Record<string, unknown>;
    useHistoryBack?: boolean;
  };
  if (returnTab === 'history' && clientId !== undefined && clientId !== '') {
    backTo = {
      to: '/app/clients/$clientId',
      label: 'History',
      params: { clientId },
      search: { tab: 'history' },
      useHistoryBack: true,
    };
  } else if (returnTab === 'sessions') {
    backTo = { to: '/app/sessions', label: 'Sessions', useHistoryBack: true };
  } else {
    backTo = { to: '/app/assessments/assignments', label: 'Assignments' };
  }
  return (
    <AssignmentResultScreen
      assessmentId={assessmentId}
      assignmentId={assignmentId}
      clientId={clientId}
      surface="clinical"
      backTo={backTo}
    />
  );
}
